import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import outletsData from "../data/barbequeen-outlets-detailed.json";
import SEO from "../components/SEO";
import { outletsPageSchema } from "../data/schemas";
import FAQSection from "../components/FAQSection";
import { outletsFaqs } from "../data/faqs";

const outlets = outletsData.outlets;

/* ── Unique cities for filter ── */
const ALL_CITIES = ["All", ...Array.from(new Set(outlets.map((o) => o.city))).sort()];

/* ── Outlet Card ── */
const OutletCard = ({ outlet }) => {
  return (
    <div className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col">

      {/* Embedded Map — always visible */}
      <div className="relative overflow-hidden" style={{ height: "200px" }}>
        <iframe
          src={outlet.mapEmbedUrl}
          title={outlet.name}
          className="w-full h-full border-0"
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        {/* City badge over the map */}
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-[#9b1c2e] text-white text-[10px] font-bold tracking-wider uppercase rounded-full z-20 shadow-md">
          {outlet.city}
        </div>
      </div>

      {/* Card Body */}
      <div className="p-5 flex flex-col flex-1">
        <h3
          className="text-[15px] font-bold text-gray-900 leading-snug mb-3 group-hover:text-[#9b1c2e] transition-colors"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {outlet.name}
        </h3>

        {/* Address */}
        <div className="flex gap-2.5 items-start mb-3">
          <svg className="w-4 h-4 text-[#9b1c2e] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-xs text-gray-500 leading-relaxed flex-1">{outlet.address}</p>
        </div>

        {/* Phone */}
        <div className="flex gap-2.5 items-center mb-4">
          <svg className="w-4 h-4 text-[#9b1c2e] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <a href={`tel:${outlet.phone.replace(/\s+/g, "")}`} className="text-sm font-semibold text-gray-700 hover:text-[#9b1c2e] transition-colors">
            {outlet.phone}
          </a>
        </div>

        {/* Action buttons */}
        <div className="mt-auto flex gap-2">
          <a
            href={outlet.directionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#9b1c2e] hover:bg-[#7d1624] text-white text-xs font-bold tracking-wide uppercase rounded-lg transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Directions
          </a>
          <a
            href={`tel:${outlet.phone.replace(/\s+/g, "")}`}
            className="flex items-center justify-center gap-1.5 px-4 py-2.5 border border-gray-200 hover:border-[#9b1c2e] text-gray-600 hover:text-[#9b1c2e] text-xs font-bold uppercase rounded-lg transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Call
          </a>
        </div>
      </div>
    </div>
  );
};

/* ── Main Page ── */
const OutletsPage = () => {
  const [activeCity, setActiveCity] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() =>
    outlets.filter((o) => {
      const matchCity = activeCity === "All" || o.city === activeCity;
      const q = search.toLowerCase();
      const matchSearch = !q || o.name.toLowerCase().includes(q) || o.city.toLowerCase().includes(q) || o.address.toLowerCase().includes(q);
      return matchCity && matchSearch;
    }),
    [activeCity, search]
  );

  return (
    <div className="min-h-screen bg-[#f9f5f0]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        title="Barbequeen Restaurant Outlets | Salem, Coimbatore, Hosur & Pudukkottai"
        metaTitle="Barbequeen Restaurant Outlets | Salem, Coimbatore, Hosur & Pudukkottai"
        description="Find all Barbequeen Restaurant outlets in Salem, Coimbatore, Erode, Hosur, Pudukkottai, and across Tamil Nadu. Get branch addresses, contact phone numbers, and directions."
        keywords="Barbequeen outlets, Barbequeen near me, Barbequeen Salem branches, Barbequeen Coimbatore, Barbequeen Erode, Barbequeen Hosur, Barbequeen Pudukkottai, Barbequeen phone numbers, Barbequeen locations, barbecue restaurant Tamil Nadu"
        robots="index, follow"
        canonical="https://barbequeen.com/outlets"
        og={{
          type: "website",
          url: "https://barbequeen.com/outlets",
          title: "Barbequeen Restaurant Outlets | Find a Branch Near You",
          description: "Locate all Barbequeen outlets in Salem, Coimbatore, Erode, Hosur, Thanjavur, Pudukkottai, and across Tamil Nadu. Contact info, addresses, and navigation.",
          image: "https://barbequeen.com/images/outlets-banner.jpg",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN"
        }}
        schema={outletsPageSchema}
      />

      {/* ── Hero Banner ── */}
      <section className="relative overflow-hidden" style={{ minHeight: "340px" }}>
        {/* Background restaurant facade/dining image */}
        <img
          src="https://barbequeen.com/public/uploads/04-14-2023-1681461440.jpg"
          alt="Our Outlets"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(10,0,2,0.72) 0%, rgba(10,0,2,0.82) 100%)" }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p className="text-xs font-bold tracking-[0.5em] uppercase text-[#c0424e] mb-4">Find Us Near You</p>
          <h1 className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>
            Our Outlets
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed mb-8">
            {outletsData.totalOutlets} locations across Tamil Nadu — find the Barbequeen nearest to you.
          </p>

          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <input
              type="text"
              placeholder="Search by city or area…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-5 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#9b1c2e] focus:bg-white/15 transition-all duration-200 backdrop-blur-sm"
            />
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </section>


      {/* ── City Filter ── */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto py-3 scrollbar-none">
            {ALL_CITIES.map((city) => (
              <button
                key={city}
                onClick={() => setActiveCity(city)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold tracking-wide uppercase transition-all duration-200 border ${
                  activeCity === city
                    ? "bg-[#9b1c2e] text-white border-[#9b1c2e]"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                }`}
              >
                {city}
                {city === "All" && <span className="ml-1 opacity-60">({outlets.length})</span>}
              </button>
            ))}
            <span className="ml-auto flex-shrink-0 text-xs text-gray-400 pl-4 whitespace-nowrap">
              {filtered.length} outlet{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-12">
        {filtered.length === 0 ? (
          <div className="text-center py-24 space-y-4">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-gray-500">No outlets found for your search.</p>
            <button onClick={() => { setActiveCity("All"); setSearch(""); }} className="text-xs font-bold tracking-widest uppercase text-[#9b1c2e] hover:underline">
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((outlet) => (
              <OutletCard key={outlet.id} outlet={outlet} />
            ))}
          </div>
        )}

        {/* CTA Banner */}
        <div className="mt-16 relative rounded-2xl overflow-hidden bg-[#0e0003] p-10 text-center">
          <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(ellipse at center, #9b1c2e 0%, transparent 70%)" }} />
          <div className="relative z-10">
            <p className="text-xs font-bold tracking-[0.4em] uppercase text-[#c0424e] mb-3">Can't Find One Near You?</p>
            <h2 className="text-3xl font-bold text-white uppercase tracking-wide mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Order Online
            </h2>
            <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed mb-8">
              Get Barbequeen's signature flavours delivered fresh to your doorstep, anywhere.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/menu"
                className="px-8 py-3.5 bg-[#9b1c2e] hover:bg-[#7d1624] text-white text-sm font-bold tracking-widest uppercase transition-colors rounded-full"
              >
                Order Now
              </Link>
              <a
                href="tel:+919597228788"
                className="flex items-center gap-2 px-8 py-3.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-bold uppercase transition-colors rounded-full backdrop-blur-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                +91 95972 28788
              </a>
            </div>
          </div>
        </div>
      </div>

      <FAQSection faqs={outletsFaqs} />
    </div>
  );
};

export default OutletsPage;

